'use client';

/**
 * SecondaryNavColumn — wrapper shared pentru coloanele laterale de tip
 * "secondary navigation" (sub-meniu pe pagină) — folosit în /setari și
 * /nomenclatoare. Centralizează:
 *
 *  - Lățimea coloanei (lg:w-52 = 208px)
 *  - Negative margin (lg:-ml-6) ca să anulăm padding-ul stâng al <main>
 *    și SettingsNav/NomenclatoareNav să fie LIPITE de sidebar principal
 *  - Sticky positioning cu top calculat dinamic (când conținutul e mai
 *    înalt decât viewport-ul, sticky se ancorează jos cu margine)
 *  - Layout mobile (horizontal scroll) vs desktop (vertical column)
 *  - Compensare scrollbar absent (lg:pr-1.5) pentru paritate vizuală
 *    cu sidebar-ul principal care are scrollbar
 *
 * Unde NU se folosește:
 *  - Sidebar principal (apps/app/src/shared/components/layout/sidebar.tsx)
 *    — are layout complex (collapsible groups, pinned section, search,
 *    collapsed mode) și e propriu component cu pattern diferit.
 *
 * Toate paginile dashboard cu un sub-meniu lateral (settings, nomenclatoare,
 * viitor: configurări curierie, etc.) trebuie să folosească acest component.
 */

import { useRef, useEffect, useState, type ReactNode } from 'react';

interface SecondaryNavColumnProps {
  ariaLabel: string;
  children: ReactNode;
}

export function SecondaryNavColumn({ ariaLabel, children }: SecondaryNavColumnProps) {
  const navRef = useRef<HTMLElement | null>(null);
  const [stickyTop, setStickyTop] = useState<number>(16);

  // Calcul dinamic stickyTop: când coloana e mai înaltă decât scroll-parent-ul,
  // ancorăm jos (cu margin), altfel sus.
  useEffect(() => {
    const TOP_OFFSET = 16;
    const BOTTOM_OFFSET = 24;
    function findScrollParent(node: HTMLElement | null): HTMLElement | null {
      let cur: HTMLElement | null = node?.parentElement ?? null;
      while (cur) {
        const style = getComputedStyle(cur);
        if (/(auto|scroll|overlay)/.test(style.overflowY)) return cur;
        cur = cur.parentElement;
      }
      return null;
    }
    const recalc = () => {
      const el = navRef.current;
      if (!el) return;
      if (!window.matchMedia('(min-width: 1024px)').matches) {
        setStickyTop(TOP_OFFSET);
        return;
      }
      const sH = el.offsetHeight;
      const scrollParent = findScrollParent(el);
      const vH = scrollParent ? scrollParent.clientHeight : window.innerHeight;
      if (sH + TOP_OFFSET <= vH) setStickyTop(TOP_OFFSET);
      else setStickyTop(vH - sH - BOTTOM_OFFSET);
    };
    const raf = requestAnimationFrame(recalc);
    window.addEventListener('resize', recalc);
    let ro: ResizeObserver | null = null;
    if (navRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(recalc);
      ro.observe(navRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', recalc);
      ro?.disconnect();
    };
  }, []);

  return (
    <nav
      ref={navRef}
      aria-label={ariaLabel}
      // lg:-ml-3 anulează padding-ul stâng al <main p-2 sm:p-3> ca această
      // coloană să fie aliniată cu marginea sidebar-ului principal. Trebuie sincronizat
      // cu padding-ul <main> din dashboard layout — dacă acela se schimbă, ajustăm aici.
      className="lg:w-52 flex-shrink-0 lg:sticky lg:self-start lg:-ml-3"
      style={{ top: `${stickyTop}px` }}
    >
      {/* Wrapper layout: pe mobile horizontal scroll (gap-3 pentru pill-style
          tabs), pe desktop vertical column tight (lg:gap-0.5) ca să match-uiască
          ritmul main sidebar-ului — grupurile din settings rămân
          distinguibile via header label (uppercase tracking-wider).
          lg:pr-1.5 = 6px compensare scrollbar absent vs main sidebar care are
          scrollbar (~6px). */}
      <div className="flex lg:flex-col gap-3 lg:gap-0.5 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 lg:pr-1.5">
        {children}
      </div>
    </nav>
  );
}
